import type { NewSelectMultiSelectOption } from '../../../Components/PIM/common/NewSelectMultiSelect/NewSelectMultiSelect';

/**
 * One assignment grant: `true` is the all-access sentinel, otherwise the picked option values.
 */
export type AssignmentValue = true | Array<string | number>;

/**
 * The four grants a supplier user carries.
 */
export type AssignmentField = 'brand' | 'subBrand' | 'category1' | 'category2';

/**
 * The grants being edited, either for one row or for the bulk editor.
 */
export type AssignmentDraft = Partial<Record<AssignmentField, AssignmentValue>>;

/**
 * A stored grant resolved to display labels.
 */
export interface ResolvedAssignment {
  isAll: boolean;
  labels: string[];
}

/**
 * Resolved grants keyed by user id, then by assignment field.
 */
export type AssignmentsByUser = Record<string, Partial<Record<AssignmentField, ResolvedAssignment>>>;

/**
 * Reads the picked option values out of a multi-select change.
 * @param {NewSelectMultiSelectOption | ReadonlyArray<NewSelectMultiSelectOption> | null} picked - The select's new value.
 * @returns {Array<string | number>} The picked values.
 */
export function toPickedValues(picked: NewSelectMultiSelectOption | ReadonlyArray<NewSelectMultiSelectOption> | null): Array<string | number> {
  const list: ReadonlyArray<NewSelectMultiSelectOption> = picked && 'length' in picked ? picked : [];
  return list.map((option) => option?.value);
}

/**
 * A sub-brand choice, carrying the brand it belongs to so the list can follow the brand grant.
 */
export interface SubBrandOption extends NewSelectMultiSelectOption {
  brandId?: string;
}

/**
 * Narrows the sub-brand choices to the brands granted. All-access, or no brand
 * picked yet, leaves every sub-brand on offer.
 * @param {SubBrandOption[] | undefined} subBrandOptions - Every active sub-brand.
 * @param {AssignmentValue | undefined} brandGrant - The brand grant being edited.
 * @returns {SubBrandOption[]} The sub-brands that belong to the granted brands.
 */
export function filterSubBrandsByBrand(subBrandOptions: SubBrandOption[] | undefined, brandGrant: AssignmentValue | undefined): SubBrandOption[] {
  const options = subBrandOptions ?? [];
  if (brandGrant === true || !Array.isArray(brandGrant) || brandGrant.length === 0) return options;
  const brandIds = brandGrant.map(String);
  return options.filter((option) => brandIds.includes(String(option?.brandId)));
}

/**
 * Drops picked sub-brands whose brand is no longer granted, so a brand change cannot
 * leave hidden sub-brand grants behind.
 * @param {AssignmentValue | undefined} subBrandGrant - The sub-brand grant being edited.
 * @param {AssignmentValue | undefined} brandGrant - The new brand grant.
 * @param {SubBrandOption[] | undefined} subBrandOptions - Every active sub-brand.
 * @returns {AssignmentValue | undefined} The sub-brand grant that still applies.
 */
export function pruneSubBrandsToBrands(
  subBrandGrant: AssignmentValue | undefined,
  brandGrant: AssignmentValue | undefined,
  subBrandOptions: SubBrandOption[] | undefined,
): AssignmentValue | undefined {
  if (!Array.isArray(subBrandGrant)) return subBrandGrant;
  const allowed = new Set(filterSubBrandsByBrand(subBrandOptions, brandGrant).map((option) => String(option?.value)));
  return subBrandGrant.filter((subBrandId) => allowed.has(String(subBrandId)));
}

/**
 * The four grants as the rights endpoint stores them: `true` for all access, otherwise a list of ids.
 */
export interface SavedGrants {
  brands?: unknown;
  subBrands?: unknown;
  typeOneCategories?: unknown;
  typeTwoCategories?: unknown;
}

/**
 * Converts a user's saved rights into the four-grant draft the editors use.
 * @param {SavedGrants | undefined} rights - The user's saved rights.
 * @returns {AssignmentDraft} The draft.
 */
export function rightsToAssignmentDraft(rights: SavedGrants | undefined): AssignmentDraft {
  return {
    brand: toAssignmentValue(rights?.brands),
    subBrand: toAssignmentValue(rights?.subBrands),
    category1: toAssignmentValue(rights?.typeOneCategories),
    category2: toAssignmentValue(rights?.typeTwoCategories),
  };
}

/**
 * Overlays only the edited grants onto a user's saved grants.
 * @param {AssignmentDraft} saved - The user's saved grants.
 * @param {AssignmentDraft} draft - The values entered in the global editor.
 * @param {AssignmentField[]} touchedFields - The grants the editor changed.
 * @returns {AssignmentDraft} The grants to write for that user.
 */
export function mergeTouchedGrants(saved: AssignmentDraft, draft: AssignmentDraft, touchedFields: AssignmentField[]): AssignmentDraft {
  return touchedFields.reduce<AssignmentDraft>((merged, field) => ({ ...merged, [field]: draft[field] }), saved);
}

/**
 * Normalises one stored grant into the draft shape: `true` stays the all-access
 * sentinel, an id list becomes strings, anything else is an empty grant.
 * @param {unknown} assigned - The grant as the rights endpoint returned it.
 * @returns {AssignmentValue} The draft value.
 */
function toAssignmentValue(assigned: unknown): AssignmentValue {
  if (assigned === true) return true;
  return Array.isArray(assigned) ? assigned.map(String) : [];
}
